/**
 * Digital-sizer auth + HTTP client (replaces Stytch).
 *
 * Token store mirrors digital_sizer/src/lib/api.ts: a JWT in localStorage
 * (`auth_token`), an optional impersonation JWT in sessionStorage that takes
 * precedence, and an `X-Role` header (default "admin"). `dsHttp` is the shared
 * axios instance for the digital-sizer backend — it injects the bearer token +
 * role on every request and bounces to the registered 401 handler.
 */
import axios from "axios";

// eastview-los backend. Served at root (no `/api` prefix): endpoints are
// `/auth/*`, `/loportal/*`, `/checklists/*`, etc. — so dsHttp paths omit `/api`.
export const DIGITAL_SIZER_BASE_URL =
  "https://eastview-los.bluebay-5ecdbe39.eastus2.azurecontainerapps.io";

const TOKEN_KEY = "auth_token";
const IMPERSONATION_KEY = "impersonation_token";
const ROLE_KEY = "role";

// ── Token store ────────────────────────────────────────────────────────────
export const getAuthToken = () => {
  try {
    const imp = sessionStorage.getItem(IMPERSONATION_KEY);
    if (imp) return imp;
  } catch {
    // ignore
  }
  try {
    // "Remember me" tokens live in localStorage; session-only tokens (remember
    // unchecked) live in sessionStorage and are dropped when the browser closes.
    return localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
};

// `options.remember`: true → persist in localStorage (survives browser close),
// false → sessionStorage (cleared on close). When omitted (e.g. a silent token
// refresh), keep the token wherever it already lives so the user's choice sticks.
export const setAuthToken = (token, options) => {
  try {
    if (!token) {
      localStorage.removeItem(TOKEN_KEY);
      sessionStorage.removeItem(TOKEN_KEY);
      return;
    }
    const remember =
      options && typeof options.remember === "boolean"
        ? options.remember
        : !sessionStorage.getItem(TOKEN_KEY);
    if (remember) {
      localStorage.setItem(TOKEN_KEY, token);
      sessionStorage.removeItem(TOKEN_KEY);
    } else {
      sessionStorage.setItem(TOKEN_KEY, token);
      localStorage.removeItem(TOKEN_KEY);
    }
  } catch {
    // ignore
  }
};

export const setImpersonationToken = (token) => {
  try {
    if (token) sessionStorage.setItem(IMPERSONATION_KEY, token);
    else sessionStorage.removeItem(IMPERSONATION_KEY);
  } catch {
    // ignore
  }
};

export const isImpersonating = () => {
  try {
    return Boolean(sessionStorage.getItem(IMPERSONATION_KEY));
  } catch {
    return false;
  }
};

export const getRole = () => {
  try {
    return localStorage.getItem(ROLE_KEY) || "admin";
  } catch {
    return "admin";
  }
};

export const setRole = (role) => {
  try {
    if (role) localStorage.setItem(ROLE_KEY, role);
  } catch {
    // ignore
  }
};

// ── 401 handler (registered by AuthProvider) ────────────────────────────────
let onUnauthorized = null;
export const setUnauthorizedHandler = (handler) => {
  onUnauthorized = handler;
};
export const notifyUnauthorized = () => {
  if (onUnauthorized) onUnauthorized();
};

// ── Shared axios instance ────────────────────────────────────────────────────
export const dsHttp = axios.create({ baseURL: DIGITAL_SIZER_BASE_URL });

dsHttp.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  config.headers["X-Role"] = getRole();
  return config;
});

// Auth 401 / transient handling — see eastview-os
// frontend_documentation/auth-login-and-password-setup.md ("DO NOT sign users
// out on transient failures"). The server session is durable (Mongo-backed, 48h)
// so a deploy NEVER invalidates it. Rules (same as losHttp):
//   • network / timeout / 5xx → transient. Never clear tokens, never sign out.
//   • any API 401 → refresh ONCE with the current token, then replay.
//   • sign out ONLY when /auth/refresh itself returns HTTP 401.
let refreshPromise = null;

const isRefreshCall = (config) => /\/auth\/refresh/.test(config?.url || "");

// Single-flight refresh via raw axios (not through dsHttp, to avoid re-entering
// this interceptor). Resolves to the new token (or null), or rejects with the
// refresh error so the caller can tell a 401 from a transient failure.
const runRefresh = () => {
  if (!refreshPromise) {
    refreshPromise = axios
      .post(
        `${DIGITAL_SIZER_BASE_URL}/auth/refresh`,
        {},
        {
          headers: {
            Authorization: `Bearer ${getAuthToken()}`,
            "X-Role": getRole(),
          },
        },
      )
      .then((r) => r?.data?.session_token ?? r?.data?.access_token ?? null)
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

dsHttp.interceptors.response.use(
  (res) => res,
  async (err) => {
    const status = err?.response?.status;
    const original = err?.config;

    // Transient: no response (network/timeout) or 5xx. Never sign out.
    if (!err?.response || status >= 500) {
      return Promise.reject(err);
    }

    // Any 401 (except on the refresh call itself) → refresh once, then replay.
    if (
      status === 401 &&
      original &&
      !original._retriedRefresh &&
      getAuthToken() &&
      !isRefreshCall(original)
    ) {
      original._retriedRefresh = true;
      try {
        const next = await runRefresh();
        if (next) {
          setAuthToken(next);
          original.headers = original.headers || {};
          original.headers.Authorization = `Bearer ${next}`;
          return dsHttp(original); // retry once with the fresh token
        }
        // Refresh returned no token but didn't reject → treat as transient.
      } catch (refreshErr) {
        // Dead session ONLY when /auth/refresh returns HTTP 401. Network/5xx
        // refresh failures are transient — keep the token, don't sign out.
        // `skipAuthRedirect` callers (realtime negotiate/presence) opt out of the
        // sign-out bounce, but they still take the refresh+replay above: skipping
        // it meant an expired access token killed the live socket for good and
        // the bell sat on "Reconnecting to live updates…" until a page reload.
        if (refreshErr?.response?.status === 401 && !original?.skipAuthRedirect) {
          notifyUnauthorized();
        }
      }
      return Promise.reject(err);
    }

    return Promise.reject(err);
  },
);

// NOTE: the standalone digital-sizer-web client (`dsWebHttp` /
// DIGITAL_SIZER_WEB_BASE_URL) was removed — every feature now targets
// eastview-los via `dsHttp`. Endpoints the LOS doesn't serve yet are flagged
// "TBD" at the call site and 404 until the backend adds them.

export default dsHttp;
